import { ROUTES } from 'config';
import { ActionsMenu } from 'melp-design/components';
import { ComponentProps, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useConfirm } from 'store/confirm';
import { useModalContext } from 'store/modal';
import { NewItem, useCopyNew, useRemoveNew, useUpdateNew } from 'store/news';
import { usePredefinedToasts } from 'utils/Toast';
import {
  EMOJI_BY_REACTION,
  reactionsVariants,
  useCreateReaction,
  useDeleteReaction,
} from 'store/reactions';
import { NewsThreadModal } from 'containers/news';
import { includes } from 'utils/general';
import moment from 'moment';
import { NewNameModal } from '../../new-details/atoms';

interface Props extends Pick<NewItem, 'id' | 'name' | 'status' | 'reactions'> {
  type?: ComponentProps<typeof ActionsMenu>['type'];
}

export const NewActions: FC<Props> = ({
  id,
  name,
  status,
  reactions,
  type,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const history = useHistory();
  const { confirmation } = useConfirm();
  const { openModal } = useModalContext();

  const { mutate: copyNew, isLoading: isCopying } = useCopyNew();
  const { mutate: updateNew, isLoading: isUpdating } = useUpdateNew();
  const { mutate: removeNew, isLoading: isRemoving } = useRemoveNew();
  const { mutate: createReaction, isLoading: isCreatingReaction } =
    useCreateReaction();
  const { mutate: deleteReaction, isLoading: isDeletingReaction } =
    useDeleteReaction();

  const copyTitle = t('common.copy');
  const titlePrefix = `(${
    copyTitle.charAt(0).toUpperCase() + copyTitle.slice(1)
  })`;

  return (
    <ActionsMenu
      type={type}
      items={[
        {
          label: t('actions.comment'),
          onClick: () => {
            openModal({
              component: NewsThreadModal,
              props: {
                newId: id,
              },
              size: 'xl',
            });
          },
        },
        {
          label: t('actions.make_copy'),
          onClick: async () => {
            const response = await openModal({
              component: NewNameModal,
              props: {
                initialName: `${titlePrefix} ${name}`,
              },
            });

            if (response.action === 'CONFIRM') {
              copyNew(
                { newId: id, name: response.name },
                {
                  onSuccess: ({ data }) => {
                    predefinedToasts.success.added();
                    history.push(
                      `${ROUTES.news.details.replace(
                        ':id',
                        data.id,
                      )}?guided=true&tab=content`,
                    );
                  },
                },
              );
            }
          },
          disabled: isCopying,
        },
        ...(includes(['inactive', 'expired'], status)
          ? [
              {
                label: t('common.unhide'),
                onClick: () => {
                  updateNew(
                    {
                      newId: id,
                      update: {
                        publishedAt: moment().toISOString(true),
                        inactiveAt: null,
                      },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                      },
                    },
                  );
                },
                disabled: isUpdating,
              },
            ]
          : []),
        ...(includes(['published', 'expiresSoon'], status)
          ? [
              {
                label: t('common.hide'),
                onClick: () => {
                  updateNew(
                    {
                      newId: id,
                      update: {
                        inactiveAt: moment().toISOString(true),
                      },
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                      },
                    },
                  );
                },
                disabled: isUpdating,
              },
            ]
          : []),
        {
          label: t('actions.delete'),
          onClick: async () => {
            const { confirmed } = await confirmation(
              t('common.areYouSureToDelete', {
                title: name,
                interpolation: { escapeValue: false },
              }),
              undefined,
              'danger',
            );

            if (confirmed) {
              removeNew(
                { newId: id },
                {
                  onSuccess: () => {
                    predefinedToasts.success.deleted();
                    history.push(ROUTES.news.list);
                  },
                },
              );
            }
          },
          color: 'error',
          disabled: isRemoving,
        },
        ...(reactions.selected
          ? [
              {
                label: [
                  EMOJI_BY_REACTION[reactions.selected],
                  t(`reactions.variant.${reactions.selected}`),
                ].join(' '),
                onClick: () => {
                  deleteReaction({
                    hostId: id,
                  });
                },
                disabled: isDeletingReaction,
                color: 'primary',
              } as const,
            ]
          : [
              null,
              ...reactionsVariants.map((reaction) => ({
                label: [
                  EMOJI_BY_REACTION[reaction],
                  t(`reactions.variant.${reaction}`),
                ].join(' '),
                onClick: () => {
                  createReaction({
                    entity: 'news',
                    hostId: id,
                    emoji: reaction,
                  });
                },
                disabled: isCreatingReaction,
              })),
            ]),
      ]}
    />
  );
};
